import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { IoCaretDownSharp, IoCaretUpSharp, IoEye } from 'react-icons/io5';
import { assetUrl } from '../helper_functions/utilities';
import BuildAndTimeline from './BuildAndTimeline';
import TeamGoldChart from './TeamGoldChart';
import {
    AssetImage,
    AugmentGrid,
    ChampionAvatar,
    CompactRankLabel,
    DamageBars,
    getToneStyles,
    HighestStreakBadge,
    KdaValue,
    ParticipantItemRow,
    PerformanceRankBadge,
    RuneIcons,
    SpellIcons,
    ToneBadge,
} from './match/MatchSharedElements';
import { buttonFocusRingClass, neutralButtonOnZincSurfaceClass } from '../shared/buttonStyles';

const TEAM_HEADER_CLASS_NAME = 'border border-zinc-900 bg-black/90';
const CARD_CHROME_RIGHT_RAIL_CLASS_NAME = 'flex w-[116px] shrink-0 items-center justify-end text-right';
function buildCombatLine(player, isArena) {
    const baseLine = `${player.kills} / ${player.deaths} / ${player.assists}`;

    if (isArena || (!player.kills && !player.deaths && !player.assists)) {
        return baseLine;
    }

    return `${baseLine} (${player.killParticipation}%)`;
}

function DetailTab({ active, children, onClick, tone }) {
    const activeClassName = tone === 'defeat'
        ? '!border-rose-700/60 !bg-rose-900/35 !text-rose-200 hover:!border-rose-700/60 hover:!bg-rose-900/35 hover:!text-rose-200'
        : tone === 'victory'
            ? '!border-cyan-700/60 !bg-cyan-900/45 !text-cyan-200 hover:!border-cyan-700/60 hover:!bg-cyan-900/45 hover:!text-cyan-200'
            : '!border-zinc-700 !bg-zinc-800/90 !text-zinc-200 hover:!border-zinc-600 hover:!bg-zinc-700 hover:!text-zinc-100';

    return (
        <button
            className={`inline-flex h-7 items-center justify-center rounded-md px-3 text-[10px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors ${buttonFocusRingClass} ${active ? `border ${activeClassName}` : `border ${neutralButtonOnZincSurfaceClass}`}`}
            onClick={onClick}
            type="button"
        >
            {children}
        </button>
    );
}

function getDetailSummaryLabel(detailType) {
    if (detailType === 'build') {
        return 'Build Timeline';
    }

    if (detailType === 'teamState') {
        return 'Team Gold State';
    }

    return 'Team Breakdown';
}

function TeamObjective({ alt, iconPath, label, value }) {
    const [tooltip, setTooltip] = useState(null);

    function showTooltip(event) {
        const rect = event.currentTarget.getBoundingClientRect();
        setTooltip({
            left: Math.min(Math.max(rect.left + (rect.width / 2), 48), window.innerWidth - 48),
            top: Math.max(rect.top - 8, 12),
        });
    }

    return (
        <div
            className="group relative flex items-center"
            onMouseEnter={showTooltip}
            onMouseLeave={() => setTooltip(null)}
        >
            <div className="flex items-center gap-1 rounded-md border border-zinc-900 bg-zinc-950/95 px-1.5 py-1 text-[10px] font-bold text-zinc-200 transition-colors duration-150 group-hover:border-zinc-700 group-hover:bg-zinc-950">
                <span className="tabular-nums">{value}</span>
                <img alt={alt} className="h-3.5 w-3.5 brightness-0 invert opacity-80" src={`${process.env.PUBLIC_URL}${iconPath}`} />
            </div>
            {tooltip ? createPortal(
                <div
                    className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full rounded-md border border-zinc-700 bg-zinc-950/95 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-zinc-200 shadow-[0_10px_24px_-16px_rgba(0,0,0,0.95)]"
                    style={{ left: tooltip.left, top: tooltip.top }}
                >
                    {label}
                </div>,
                document.body,
            ) : null}
        </div>
    );
}

function TeamBanStrip({ bans, championNameIdById }) {
    const normalizedBans = [...(bans || [])].sort((left, right) => (left?.pickTurn || 0) - (right?.pickTurn || 0)).slice(0, 5);

    while (normalizedBans.length < 5) {
        normalizedBans.push({
            championId: 0,
            pickTurn: normalizedBans.length + 1,
        });
    }

    return (
        <div className="flex items-center gap-1">
            {normalizedBans.map((ban, index) => {
                const championNameId = championNameIdById[ban?.championId] || '';
                const hasChampionBan = Number(ban?.championId) > 0;

                return (
                    <div className="h-7 w-7 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900" key={`${ban?.pickTurn || index}-${ban?.championId || 0}-${index}`}>
                        <AssetImage
                            alt={hasChampionBan ? 'banned champion icon' : 'empty ban slot'}
                            className={`h-full w-full object-cover ${hasChampionBan ? 'scale-[1.22] grayscale' : 'scale-[0.92] opacity-55'}`}
                            src={hasChampionBan ? assetUrl(`/champion_icons/${championNameId}.png`) : `${process.env.PUBLIC_URL}/assets/missing_asset/missing_icon.png`}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function CompactStatRow({ icon, iconClassName = 'h-4 w-4', value }) {
    return (
        <div className="grid h-5 w-full grid-cols-[14px_1fr] items-center gap-x-1 overflow-hidden pl-1 text-[10px] font-medium leading-none text-zinc-300">
            <img alt="" className={`${iconClassName} shrink-0 self-center filter brightness-0 invert opacity-80`} src={icon} />
            <span className="flex min-w-0 items-center justify-end text-right font-semibold leading-none text-zinc-200">{value}</span>
        </div>
    );
}

function TeamPanel({ championNameIdById, displayRegion, isFirstTeamPanel, isLastTeamPanel, navAndSearchParticipant, team }) {
    return (
        <div>
            <div className={`grid grid-cols-[auto_auto_auto_minmax(0,1fr)_auto] items-center gap-3 px-2.5 py-2 text-sm ${TEAM_HEADER_CLASS_NAME} ${isFirstTeamPanel ? 'rounded-t-lg' : ''}`}>
                <div className="flex min-w-0 items-center">
                    <ToneBadge compact label={team.outcomeLabel} tone={team.tone} />
                </div>

                <div className="flex items-center">
                    <div className="flex items-center gap-1 rounded-md border border-zinc-900 bg-zinc-950/95 px-2 py-1 text-[10px] font-bold text-zinc-200">
                        <img
                            alt="kda icon"
                            className="h-3.5 w-3.5 filter brightness-0 invert opacity-80"
                            src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-kda-icon.svg`}
                        />
                        <div className="tabular-nums">{team.kdaLine}</div>
                    </div>
                </div>

                <div className="flex min-w-0 items-center">
                    <CompactRankLabel badge={team.averageElo?.badge} leaguePoints={team.averageElo?.leaguePoints} />
                </div>

                <div className="flex min-w-0 items-center justify-center">
                    <TeamBanStrip bans={team.bans} championNameIdById={championNameIdById} />
                </div>

                <div className="flex min-w-0 items-center justify-end gap-1.5 text-[11px] font-normal text-zinc-300">
                    <TeamObjective alt="tower icon" iconPath="/assets/minimap_asset_icons/icon_ui_tower_minimap.png" label="Turrets" value={team.objectives.turrets} />
                    <TeamObjective alt="dragon icon" iconPath="/assets/minimap_asset_icons/dragon_minimap_icon.png" label="Dragons" value={team.objectives.dragons} />
                    <TeamObjective alt="void grub icon" iconPath="/assets/minimap_asset_icons/horde.png" label="Void Grubs" value={team.objectives.voidGrubs} />
                    <TeamObjective alt="rift herald icon" iconPath="/assets/minimap_asset_icons/sru_riftherald_minimap_icon.png" label="Rift Heralds" value={team.objectives.riftHeralds} />
                    <TeamObjective alt="baron icon" iconPath="/assets/minimap_asset_icons/baron_minimap_icon.png" label="Barons" value={team.objectives.barons} />
                </div>
            </div>

            {team.participants.map((participant, index) => {
                const toneStyles = getToneStyles(participant.tone);
                const rowSurface = participant.isFocus ? toneStyles.rowHighlightSurface : 'bg-black border-zinc-800';

                return (
                    <div
                        className={`border-l border-r border-t px-2.5 py-1.5 ${rowSurface} ${
                            index === team.participants.length - 1 && isLastTeamPanel ? 'rounded-b-lg border-b' : ''
                        }`}
                        key={participant.puuid}
                    >
                        <div className="grid h-[44px] grid-cols-[174px_128px_64px_188px_6px_78px] grid-rows-[20px_20px] gap-y-1 text-xs">
                            <div className="grid min-w-[174px] grid-cols-[42px_42px_78px] grid-rows-[20px_20px] items-center gap-x-1.5 gap-y-1 pr-3">
                                <div className="row-span-2 flex h-full items-center justify-center self-center">
                                    <ChampionAvatar
                                        championIconSrc={participant.championIconSrc}
                                        championLevel={participant.championLevel}
                                        imageSize={42}
                                        levelBadgeClassName="translate-x-[2px] translate-y-[2px]"
                                        levelBadgeSize={15}
                                        tone={participant.tone}
                                    />
                                </div>
                                <div className="col-start-2 row-start-1 flex h-full items-center justify-center self-center">
                                    <SpellIcons size={20} sources={participant.spellSources} tone={participant.tone} />
                                </div>
                                <div className="col-start-2 row-start-2 flex h-full items-center justify-center self-center">
                                    <RuneIcons
                                        secondaryClassName="scale-[0.72] object-contain"
                                        secondaryInsetClassName="p-[2px]"
                                        size={20}
                                        sources={participant.runeSources}
                                        tone={participant.tone}
                                    />
                                </div>
                                <div className="col-start-3 row-span-2 flex h-full items-center self-center">
                                    <DamageBars
                                        className="w-[78px]"
                                        compact
                                        dealtLabel={participant.damageDealtLabel}
                                        dealtPercent={participant.damageDealtPercent}
                                        takenLabel={participant.damageTakenLabel}
                                        takenPercent={participant.damageTakenPercent}
                                        tone={participant.tone}
                                    />
                                </div>
                            </div>

                            <div className="grid min-w-[128px] grid-rows-[20px_20px] gap-y-1 pl-1">
                                <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_58px] items-center gap-2 overflow-hidden">
                                    <Link
                                        className="block min-w-0 flex-1 truncate self-center font-semibold text-zinc-100"
                                        onClick={navAndSearchParticipant}
                                        to={`/summoners/${displayRegion}/${participant.linkSlug}`}
                                    >
                                        {participant.gameName}
                                    </Link>
                                    <div className="flex items-center justify-end">
                                        <CompactRankLabel
                                            badge={participant.eloBadge}
                                            className="shrink-0 !text-[10px]"
                                            leaguePoints={participant.eloLeaguePoints}
                                        />
                                    </div>
                                </div>
                                <div className="flex min-w-0 items-center justify-between gap-2 overflow-visible uppercase tracking-[0.14em]">
                                    <span className="shrink-0 text-[9px] font-bold text-zinc-300">Lvl. {participant.summonerLevel}</span>
                                    {participant.roleStatusLabel ? (
                                        <span className="shrink-0 text-right text-[8.5px] font-medium uppercase tracking-[0.14em] text-zinc-300/60">
                                            {participant.roleStatusLabel}
                                        </span>
                                    ) : <span className="shrink-0" />}
                                </div>
                            </div>

                            <div className="col-start-3 grid grid-rows-[20px_20px] justify-items-center gap-y-1">
                                <div className="flex h-full w-full items-center justify-center overflow-visible">
                                    <PerformanceRankBadge compact rank={participant.performanceRank} />
                                </div>
                                <div className="flex h-full w-full items-center justify-center overflow-visible">
                                    {participant.highestStreak ? <HighestStreakBadge className="px-1 py-[1px] text-[8px] tracking-[0.1em]" highestStreak={participant.highestStreak} /> : null}
                                </div>
                            </div>

                            <div className="col-start-4 grid w-[188px] shrink-0 grid-rows-[20px_20px] gap-y-1 py-[1px]">
                                <div className="flex w-[188px] items-center">
                                    <ParticipantItemRow
                                        className="w-max"
                                        extraItemSource={participant.roleBoundItemSource}
                                        itemSources={participant.itemSources.concat(participant.trinketSource)}
                                        size={20}
                                        tone={participant.tone}
                                    />
                                </div>
                                <div className="grid w-[188px] grid-cols-[minmax(0,1fr)_36px] items-center gap-x-6 whitespace-nowrap text-[10px] leading-none text-zinc-300">
                                    <div className="grid h-5 min-w-0 grid-cols-[12px_minmax(0,1fr)] items-center gap-x-1 overflow-hidden text-[10px] font-medium leading-none text-zinc-300">
                                        <img
                                            alt=""
                                            className="h-3 w-3 shrink-0 self-center filter brightness-0 invert"
                                            src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-kda-icon.svg`}
                                        />
                                        <span className="flex min-w-0 items-center justify-end gap-0.5 font-semibold leading-none text-zinc-200">
                                            <span className="truncate leading-none">{buildCombatLine(participant, false)}</span>
                                            <span aria-hidden="true" className="inline-flex shrink-0 items-center leading-none text-zinc-300">·</span>
                                            <KdaValue className="shrink-0 leading-none tabular-nums" iconSize={12} isPerfectKda={participant.isPerfectKda} value={participant.kda} />
                                        </span>
                                    </div>
                                    <div className="grid h-5 w-full grid-cols-[12px_minmax(0,1fr)] items-center gap-x-1 overflow-hidden text-[10px] font-medium leading-none text-zinc-300">
                                        <IoEye className="h-3 w-3 shrink-0 self-center" />
                                        <span className="flex min-w-0 items-center justify-end text-right font-semibold leading-none text-zinc-200">{participant.visionScore}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="col-start-6 grid w-[78px] shrink-0 grid-rows-[20px_20px] gap-y-1 py-[1px] text-[10px] text-zinc-300">
                                <CompactStatRow
                                    icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/minions-slain.svg`}
                                    value={`${participant.totalCs} · ${participant.csPerMinute.toFixed(1)}`}
                                />
                                <CompactStatRow
                                    icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-coins-icon.svg`}
                                    value={`${participant.goldLabel} · ${participant.goldPerMinute.toFixed(0)}`}
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ArenaParticipantRow({ displayRegion, index, isLast, navAndSearchParticipant, participant }) {
    const rowTone = participant.arenaPlacementTone;
    const toneStyles = getToneStyles(rowTone);
    const rowSurface = participant.isFocus ? toneStyles.rowHighlightSurface : toneStyles.rowSurface;

    return (
        <div className={`border-l border-r border-t ${rowSurface} ${isLast ? 'rounded-b-lg border-b' : ''}`}>
            {index % 2 === 0 ? (
                <div className={`flex items-center gap-3 px-2 py-1.5 ${TEAM_HEADER_CLASS_NAME}`}>
                    <ToneBadge compact label={participant.placementLabel} tone={rowTone} />
                    <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-300">Team {participant.arenaTeamName}</div>
                    <img
                        alt="arena team icon"
                        height="16"
                        src={`${process.env.PUBLIC_URL}/assets/arena_team_icons/Team${participant.arenaTeamName}.png`}
                        width="16"
                    />
                </div>
            ) : null}

            <div className="px-2 py-1">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2 text-sm">
                        <CompactRankLabel badge={participant.eloBadge} leaguePoints={participant.eloLeaguePoints} />
                        <Link
                            className="truncate font-bold text-zinc-100"
                            onClick={navAndSearchParticipant}
                            to={`/summoners/${displayRegion}/${participant.linkSlug}`}
                        >
                            {participant.gameName}
                        </Link>
                    </div>
                    <ParticipantItemRow
                        includeExtraItem={false}
                        itemSources={participant.itemSources.concat(participant.trinketSource)}
                        size={18}
                        tone={rowTone}
                    />
                </div>

                    <div className="mt-2 flex items-start justify-between gap-3 text-xs text-zinc-300">
                        <div className="flex gap-2">
                            <ChampionAvatar
                                championIconSrc={participant.championIconSrc}
                                championLevel={participant.championLevel}
                                imageSize={34}
                                levelBadgeClassName="translate-x-[2px] translate-y-[2px]"
                                levelBadgeSize={16}
                                tone={rowTone}
                            />
                        <div className="flex gap-1">
                            <div className="flex flex-col gap-0.5 pt-0.5">
                                <SpellIcons size={16} sources={participant.spellSources} tone={rowTone} />
                            </div>
                            <AugmentGrid columns={3} size={15} sources={participant.augmentSources} tone={rowTone} />
                        </div>
                    </div>

                    <div className="w-[154px] shrink-0">
                        <div className="flex items-center gap-1">
                            <img
                                alt="kda icon"
                                className="h-3 w-3 filter brightness-0 invert"
                                src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-kda-icon.svg`}
                            />
                            <span>{buildCombatLine(participant, true)}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-1">
                            <KdaValue iconSize={12} isPerfectKda={participant.isPerfectKda} value={participant.kda} />
                            <span>KDA</span>
                        </div>
                    </div>

                    <div className="w-[80px] shrink-0">
                        <div className="flex items-center gap-1">
                            <img
                                alt="coins icon"
                                className="h-4 w-4 filter brightness-0 invert"
                                src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-coins-icon.svg`}
                            />
                            <span>{participant.goldLabel}</span>
                        </div>
                    </div>

                    <DamageBars
                        className="w-[100px] shrink-0"
                        compact
                        dealtLabel={participant.damageDealtLabel}
                        dealtPercent={participant.damageDealtPercent}
                        takenLabel={participant.damageTakenLabel}
                        takenPercent={participant.damageTakenPercent}
                        tone={rowTone}
                    />
                </div>
            </div>
        </div>
    );
}

function ArenaTriosParticipantRow({ displayRegion, navAndSearchParticipant, participant, rowTone }) {
    return (
        <div className="px-2.5 py-1.5">
            <div className="grid h-[44px] grid-cols-[194px_128px_minmax(0,1fr)] grid-rows-[20px_20px] gap-y-1 text-xs">
                <div className="grid min-w-[194px] grid-cols-[42px_62px_78px] grid-rows-[20px_20px] items-center gap-x-1.5 gap-y-1 pr-3">
                    <div className="row-span-2 flex h-full items-center justify-center self-center">
                        <ChampionAvatar
                            championIconSrc={participant.championIconSrc}
                            championLevel={participant.championLevel}
                            imageSize={42}
                            levelBadgeClassName="translate-x-[2px] translate-y-[2px]"
                            levelBadgeSize={15}
                            tone={participant.tone}
                        />
                    </div>
                    <div className="col-start-2 row-span-2 flex h-full items-center justify-center self-center">
                        <AugmentGrid
                            className="gap-[2px] justify-items-center"
                            columns={3}
                            size={19}
                            sources={participant.augmentSources}
                            tone={rowTone}
                        />
                    </div>
                    <div className="col-start-3 row-span-2 flex h-full items-center self-center">
                        <DamageBars
                            className="w-[78px]"
                            compact
                            dealtLabel={participant.damageDealtLabel}
                            dealtPercent={participant.damageDealtPercent}
                            takenLabel={participant.damageTakenLabel}
                            takenPercent={participant.damageTakenPercent}
                            tone={rowTone}
                        />
                    </div>
                </div>

                <div className="grid min-w-[128px] grid-rows-[20px_20px] gap-y-1 pl-1">
                    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_58px] items-center gap-2 overflow-hidden">
                        <Link
                            className="block min-w-0 flex-1 truncate self-center font-semibold text-zinc-100"
                            onClick={navAndSearchParticipant}
                            to={`/summoners/${displayRegion}/${participant.linkSlug}`}
                        >
                            {participant.gameName}
                        </Link>
                        <div className="flex items-center justify-start">
                            <CompactRankLabel
                                badge={participant.eloBadge}
                                className="shrink-0"
                                leaguePoints={participant.eloLeaguePoints}
                            />
                        </div>
                    </div>
                    <div className="flex min-w-0 items-center justify-between gap-2 overflow-visible uppercase tracking-[0.14em]">
                        <span className="shrink-0 text-[9px] font-bold text-zinc-300">Lvl. {participant.summonerLevel}</span>
                        <span className="shrink-0" />
                    </div>
                </div>

                <div className="col-start-3 grid min-w-0 justify-items-end grid-rows-[20px_20px] gap-y-1 py-[1px]">
                    <div className="flex w-[188px] items-center justify-end">
                        <ParticipantItemRow
                            className="w-max"
                            includeExtraItem={false}
                            itemSources={participant.itemSources.concat(participant.trinketSource)}
                            size={20}
                            tone={participant.tone}
                        />
                    </div>
                    <div className="flex w-[84px] items-center justify-end whitespace-nowrap text-[10px] leading-none text-zinc-300">
                        <div className="grid h-5 w-[84px] grid-cols-[12px_minmax(0,1fr)] items-center gap-x-1 overflow-hidden text-[10px] font-medium leading-none text-zinc-300">
                            <img
                                alt=""
                                className="h-3 w-3 shrink-0 self-center filter brightness-0 invert"
                                src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-kda-icon.svg`}
                            />
                            <span className="flex min-w-0 items-center justify-end gap-0.5 font-semibold leading-none text-zinc-200">
                                <span className="truncate leading-none">{buildCombatLine(participant, true)}</span>
                                <span aria-hidden="true" className="inline-flex shrink-0 items-center leading-none text-zinc-300">·</span>
                                <KdaValue className="shrink-0 leading-none tabular-nums" iconSize={12} isPerfectKda={participant.isPerfectKda} value={participant.kda} />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ArenaTriosTeamCard({ displayRegion, isLast, navAndSearchParticipant, team }) {
    const rowTone = team.tone;
    const toneStyles = getToneStyles(rowTone);

    return (
        <div className={`${isLast ? '' : 'border-b border-zinc-800'} ${toneStyles.rowSurface}`}>
            <div className={`grid grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-3 px-2.5 py-2 ${TEAM_HEADER_CLASS_NAME}`}>
                <ToneBadge compact label={team.placementLabel} tone={rowTone} withDot={false} />
                <div className="flex min-w-0 items-center gap-3">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-300">Team {team.teamName}</div>
                    <img
                        alt="arena team icon"
                        height="16"
                        src={`${process.env.PUBLIC_URL}/assets/arena_team_icons/Team${team.teamName}.png`}
                        width="16"
                    />
                </div>
                <div className="min-w-0 text-right text-[11px] font-medium text-zinc-300">
                    Arena Trios
                </div>
            </div>
            {team.participants.map((participant, index) => (
                <div
                    className={`border-t border-zinc-800 ${toneStyles.rowSurface}`}
                    key={participant.puuid}
                >
                    <ArenaTriosParticipantRow
                        displayRegion={displayRegion}
                        navAndSearchParticipant={navAndSearchParticipant}
                        participant={participant}
                        rowTone={rowTone}
                    />
                </div>
            ))}
        </div>
    );
}

const MatchDetailsDesktop = ({
    displayRegion,
    entry,
    hideFooter = false,
    navAndSearchParticipant,
    onShowDetailsChange,
    orderedChampions,
    showDetails: controlledShowDetails,
}) => {
    const [internalShowDetails, setInternalShowDetails] = useState(false);
    const [detailType, setDetailType] = useState('default');
    const isDetailsControlled = typeof controlledShowDetails === 'boolean';
    const showDetails = isDetailsControlled ? controlledShowDetails : internalShowDetails;
    const toneStyles = getToneStyles(entry.tone);
    const championNameIdById = {};

    orderedChampions?.forEach((champion) => {
        championNameIdById[Number(champion?.champion_id)] = champion?.name_id;
    });

    const handleShowDetailsChange = (nextValueOrUpdater) => {
        const nextValue = typeof nextValueOrUpdater === 'function' ? nextValueOrUpdater(showDetails) : nextValueOrUpdater;

        if (!isDetailsControlled) {
            setInternalShowDetails(nextValue);
        }

        onShowDetailsChange?.(nextValue);
    };

    return (
        <div>
            {!hideFooter ? (
                <div className={`flex items-center justify-between rounded-b-lg border-t border-zinc-800/70 px-4 pt-1 pb-2 text-xxs ${toneStyles.accentText}`}>
                    <div className="flex items-center gap-2">
                        <div className="italic leading-none opacity-80">{entry.footerMatchId}</div>
                    </div>
                    <div className={CARD_CHROME_RIGHT_RAIL_CLASS_NAME}>
                        <button
                            className={`flex w-full items-center justify-end gap-1 rounded-md py-0.5 text-[10px] uppercase tracking-[0.16em] transition-colors hover:text-zinc-100 ${toneStyles.accentText}`}
                            onClick={() => handleShowDetailsChange((currentValue) => !currentValue)}
                            type="button"
                        >
                            <span>Details</span>
                            {showDetails ? (
                                <IoCaretUpSharp className={`h-3.5 w-3.5 ${toneStyles.accentText}`} />
                            ) : (
                                <IoCaretDownSharp className={`h-3.5 w-3.5 ${toneStyles.accentText}`} />
                            )}
                        </button>
                    </div>
                </div>
            ) : null}

            {showDetails ? (
                <div className="bg-zinc-900 px-3 pt-2 pb-3">
                    {entry.isArena ? (
                        <div className="mt-3 overflow-hidden rounded-xl border border-zinc-800 bg-black">
                            {entry.isTriosArena
                                ? entry.arenaTeams.map((team, index) => (
                                    <ArenaTriosTeamCard
                                        displayRegion={displayRegion}
                                        isLast={index === entry.arenaTeams.length - 1}
                                        key={`${team.subteamId}-${team.placement}`}
                                        navAndSearchParticipant={navAndSearchParticipant}
                                        team={team}
                                    />
                                ))
                                : entry.arenaParticipants.map((participant, index) => (
                                    <ArenaParticipantRow
                                        displayRegion={displayRegion}
                                        index={index}
                                        isLast={index === entry.arenaParticipants.length - 1}
                                        key={participant.puuid}
                                        navAndSearchParticipant={navAndSearchParticipant}
                                        participant={participant}
                                    />
                                ))}
                        </div>
                    ) : (
                        <div>
                            <div className="mb-3 flex items-center gap-3 px-2.5 pb-2">
                                <div className="flex flex-wrap gap-1">
                                    <DetailTab active={detailType === 'default'} onClick={() => setDetailType('default')} tone={entry.tone}>
                                        DETAILS
                                    </DetailTab>
                                    <DetailTab active={detailType === 'build'} onClick={() => setDetailType('build')} tone={entry.tone}>
                                        BUILD
                                    </DetailTab>
                                    {entry.teamState?.checkpoints?.length ? (
                                        <DetailTab active={detailType === 'teamState'} onClick={() => setDetailType('teamState')} tone={entry.tone}>
                                            TEAM
                                        </DetailTab>
                                    ) : null}
                                </div>

                                <div className="ml-auto min-w-0 text-right">
                                    <div className={`text-[10px] font-bold uppercase tracking-[0.16em] ${toneStyles.accentText}`}>
                                        {getDetailSummaryLabel(detailType)}
                                    </div>
                                    <div className="mt-0.5 truncate text-[11px] font-medium text-zinc-300">
                                        {entry.gameModeLabel} · {entry.durationLabel}
                                    </div>
                                </div>
                            </div>

                            {detailType === 'default' ? (
                                <div className="overflow-hidden rounded-xl border border-zinc-800 bg-black">
                                    {entry.teams.map((team, index) => (
                                        <TeamPanel
                                            championNameIdById={championNameIdById}
                                            displayRegion={displayRegion}
                                            isFirstTeamPanel={index === 0}
                                            isLastTeamPanel={index === entry.teams.length - 1}
                                            key={team.teamId}
                                            navAndSearchParticipant={navAndSearchParticipant}
                                            team={team}
                                        />
                                    ))}
                                </div>
                            ) : null}

                            {detailType === 'build' ? (
                                <BuildAndTimeline highlightedPlayer={entry.player.puuid} individualStats={entry.playerRaw} match={entry.rawMatch} />
                            ) : null}

                            {detailType === 'teamState' ? (
                                <TeamGoldChart
                                    playerTeamId={entry.player.teamId}
                                    teamState={entry.teamState}
                                />
                            ) : null}
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default MatchDetailsDesktop;
